const R = require('ramda')
const Ajv = require('ajv')
const removeNulls = require('./remove-nulls')
const fieldPathMatcher = require('./field-path-matcher')
const newChaos = require('./new-chaos')

const flattenObj = (obj, separator = '.') => {
    const go = obj_ => R.chain(([k, v]) => {
        if (typeof v === 'object') {
            return R.map(([k_, v_]) => [`${k}${separator}${k_}`, v_], go(v))
        } else {
            return [[k, v]]
        }
    }, R.toPairs(obj_))

    return R.fromPairs(go(obj))
}

const ajv = new Ajv({
    allErrors: true,
    jsonPointers: true,
    coerceTypes: true,
    additionalProperties: false,
    removeAdditional: true
})

ajv.addKeyword('nullable', {
    modifying: true,
    compile (schema, parent) {
        return (data, path, parent, key) => {
            return data === null === schema
        }
    },
    errors: true
})

ajv.addKeyword('notEmpty', {
    type: 'string',
    validate: function (schema, data) {
        let res = false
        if (typeof data === 'string') {
            res = data.trim() !== ''
        } else if (typeof data === 'number') {
            res = data !== null && data !== undefined && !isNaN(data)
        }

        res = data !== null

        return res
    },
    errors: true
})

ajv.addKeyword('notZero', {
    type: ['null', 'number'],
    validate: function (schema, data) {
        let res = false
        if (typeof data === 'string') {
            res = data.trim() !== ''
        } else if (typeof data === 'number') {
            res = data !== null && data !== undefined && !isNaN(data) && data !== 0
        }

        res = data !== null

        return res
    },
    errors: true
})

const schemaValidation = ajv.compile({
    $async: true,
    properties: {
        name: {
            type: 'string'
        },
        schema: {
            type: 'object'
        }
    }
})

class ValidationError extends Error {
    constructor (message, errors, data) {
        super(message)

        this.errors = errors
        this.data = data
    }
}

/**
 * Validation plugin that wraps AJV for the simplicity sake
 */
class ValidatorPlugin {
    install (Vue) {
        Vue.prototype.$chaos = function (formName, promise) {
            return newChaos(formName, promise)
        }

        Vue.prototype.$lazyValidate = function (rule, _data) {
            return this.$validate(rule, _data, false)
        }

        Vue.prototype.$validate = function (rule, _data, paranoic = true) {
            // cleans object inpurity like Vue's observers.
            const pureData = R.clone(_data)
            let data = null

            if (paranoic) {
                data = removeNulls(R.clone(pureData))
            } else {
                data = R.clone(pureData)
            }

            let options = null

            if (typeof rule === 'string') {
                if (this.$options.validators.hasOwnProperty(rule)) {
                    return Promise.reject(new Error(`No definition of ${rule} on validatiors`))
                }

                options = this.$options.validators[rule].apply(this, R.clone(data))
            } else {
                options = rule
            }

            return schemaValidation(options).then(options => {
                const schema = {
                    additionalProperties: false,
                    removeAdditional: true,
                    $async: true,
                    ...options.schema
                }

                console.log('Applying schema: %O against %O', R.clone(schema), R.clone(pureData))

                const validate = ajv.compile(schema)

                const messages = R.pipe(
                    R.toPairs,
                    R.map(([k, v]) => {
                        return {
                            path: k,
                            matcher: fieldPathMatcher(k),
                            message: v
                        }
                    }),
                    R.reverse
                )(flattenObj(options.messages, '/'))

                return validate(R.clone(data) || {})
                    .then((newData) => {
                        return newData
                    })
                    .catch(err => {
                        console.error({
                            message: `Validation error on '${options.name}'`,
                            errors: err.errors,
                            data: R.clone(data)
                        })

                        err.errors = err.errors.map((error) => {
                            let dataPath = error.dataPath
                            let errorPath = null
                            let schemaPath = error.schemaPath

                            if (error.keyword === 'required') {
                                schemaPath = `#/properties${dataPath}/${error.params.missingProperty}/required`
                                dataPath = `${error.dataPath}/${error.params.missingProperty}`
                                errorPath = `${error.dataPath}/${error.params.missingProperty}/required`
                            } else {
                                dataPath = `${error.dataPath}`
                                errorPath = `${error.dataPath}/${error.keyword}`
                            }

                            dataPath = dataPath.replace(/^\//, '')
                            errorPath = errorPath.replace(/^\//, '')

                            const fieldInfo = {
                                errorPath,
                                dataPath,
                                schemaPath
                            }

                            const x = messages.find((item) => {
                                return item.matcher.some(fieldInfo.errorPath)
                            })

                            if (x) {
                                return {
                                    ...error,
                                    ...fieldInfo,
                                    originalMessage: error.message,
                                    message: x.message
                                }
                            }

                            return {
                                ...error,
                                ...fieldInfo
                            }
                        })

                        throw new ValidationError(
                            `Validation error on '${options.name}'`,
                            err.errors,
                            R.clone(data)
                        )
                    })
            }).catch((ex) => {
                throw ex
            })
        }
    }
}

ValidatorPlugin.string = {
    type: 'string'
}

ValidatorPlugin.nonEmptyString = {
    type: 'string',
    notEmpty: true
}

ValidatorPlugin.number = {
    type: 'number'
}

ValidatorPlugin.any = {}

ValidatorPlugin.integer = {
    type: 'integer'
}

ValidatorPlugin.date = {
    type: 'string',
    format: 'date'
}

ValidatorPlugin.optionalDate = {
    type: ['string', 'null'],
    format: 'date'
}

ValidatorPlugin.datetime = {
    type: 'string',
    format: 'date-time'
}

ValidatorPlugin.optionalDatetime = {
    type: ['string', 'null'],
    format: 'date-time'
}

ValidatorPlugin.time = {
    type: 'string',
    format: 'time'
}

ValidatorPlugin.optionalTime = {
    type: ['string', 'null'],
    format: 'time'
}

ValidatorPlugin.optionalNumber = {
    type: ['number', 'null']
}

ValidatorPlugin.optionalBoolean = {
    type: ['boolean', 'null']
}

ValidatorPlugin.optionalString = {
    type: ['number', 'null']
}

ValidatorPlugin.optionalInteger = {
    type: ['integer', 'null']
}

ValidatorPlugin.boolean = {
    type: 'boolean'
}

ValidatorPlugin.arrayOf = (type) => {
    return {
        additionalProperties: false,
        $async: true,
        type: 'array',
        items: type
    }
}

ValidatorPlugin.shape = (params, required = undefined) => {
    return {
        additionalProperties: false,
        $async: true,
        type: 'object',
        properties: params,
        required
    }
}

ValidatorPlugin.optionalShape = (params, required = undefined) => {
    return {
        additionalProperties: false,
        removeAdditional: true,
        $async: true,
        type: ['object', 'null'],
        properties: params,
        required
    }
}

ValidatorPlugin.optionalArrayOf = (type) => {
    return {
        additionalProperties: false,
        removeAdditional: true,
        $async: true,
        type: ['array', 'null'],
        items: type
    }
}

ValidatorPlugin.integerOp = {
    type: 'object',
    properties: {
        eq: ValidatorPlugin.optionalInteger,
        neq: ValidatorPlugin.optionalInteger,
        gt: ValidatorPlugin.optionalInteger,
        lt: ValidatorPlugin.optionalInteger,
        gte: ValidatorPlugin.optionalInteger,
        lte: ValidatorPlugin.optionalInteger,
        in: ValidatorPlugin.optionalArrayOf(ValidatorPlugin.integer),
        notIn: ValidatorPlugin.optionalArrayOf(ValidatorPlugin.integer)
    }
}

ValidatorPlugin.numberOp = {
    type: 'object',
    properties: {
        eq: ValidatorPlugin.optionalNumber,
        neq: ValidatorPlugin.optionalNumber,
        gt: ValidatorPlugin.optionalNumber,
        lt: ValidatorPlugin.optionalNumber,
        gte: ValidatorPlugin.optionalNumber,
        lte: ValidatorPlugin.optionalNumber,
        in: ValidatorPlugin.optionalArrayOf(ValidatorPlugin.number),
        notIn: ValidatorPlugin.optionalArrayOf(ValidatorPlugin.number)
    }
}

ValidatorPlugin.stringOp = {
    type: 'object',
    properties: {
        eq: ValidatorPlugin.optionalString,
        neq: ValidatorPlugin.optionalString,
        gt: ValidatorPlugin.optionalString,
        lt: ValidatorPlugin.optionalString,
        gte: ValidatorPlugin.optionalString,
        lte: ValidatorPlugin.optionalString,
        in: ValidatorPlugin.optionalArrayOf(ValidatorPlugin.string),
        notIn: ValidatorPlugin.optionalArrayOf(ValidatorPlugin.string)
    }
}

ValidatorPlugin.option = (def) => {
    if (Array.isArray(def.type)) {
        if (def.type.indexOf('null') < 0) {
            def.type.push('null')
        }
    } else {
        def.type = [def.type, 'null']
    }

    return def
}

ValidatorPlugin.op = (type) => {
    return {
        type: 'object',
        properties: {
            eq: ValidatorPlugin.option(type),
            neq: ValidatorPlugin.option(type),
            gt: ValidatorPlugin.option(type),
            lt: ValidatorPlugin.option(type),
            gte: ValidatorPlugin.option(type),
            lte: ValidatorPlugin.option(type),
            in: ValidatorPlugin.optionalArrayOf(type),
            notIn: ValidatorPlugin.optionalArrayOf(type)
        }
    }
}

module.exports = ValidatorPlugin



// WEBPACK FOOTER //
// ./resources/assets/javascript/sci/helpers/new-validator.js